import { useTranslation } from 'react-i18next';
import { observer } from 'mobx-react-lite';
import { useNavigate, useParams } from 'react-router';
import { useStepper } from '../partials/actionStepperPartial';
import { ElementType, FC, useEffect, useState } from 'react';
import { UploadFileStep } from './steps/uploadFile';
import { MapFieldsStep } from './steps/mapFields';
import { useCreateImport } from './hooks/useCreateImport';
import { ConfirmationStep } from './steps/confirmation';
import { ImportTypeEnum } from '@pxtra-client-lib-legacy/gen';
import { StepElement } from '../../../lib/components/compound/progress/stepper';
import { useStore } from '../../../hooks/useStore';

export interface StepDef {
  content: FC;
  title?: string | null;
  sectionTitle?: string | null;
  stepperConfig?: Array<Omit<StepElement, 'icon' & { icon?: ElementType }>>;
  nextText?: string;
  hideNavigation?: boolean;
}

export const CreateImport = observer(() => {
  const { t } = useTranslation();
  const params = useParams();
  const navigate = useNavigate();
  const { setStepperConfig, setBackToPath, setBackToTitle, setTitle, setSectionTitle } =
    useStepper();
  const { newImport } = useCreateImport();
  const { impexStore } = useStore();

  const uploadFile: StepDef = {
    content: UploadFileStep,
    sectionTitle: t('form:SELECT_FILE')
  };

  const mapFields: StepDef = {
    content: MapFieldsStep,
    sectionTitle: t('form:MAP_FIELDS')
  };

  const confirm: StepDef = {
    content: ConfirmationStep,
    sectionTitle: undefined
  };

  const importStepper = [
    {
      title: t('form:SELECT_FILE')
    },
    {
      title: t('form:MAP_FIELDS')
    }
  ];

  const step: Record<'employees', Array<StepDef>> = {
    employees: [
      {
        ...uploadFile,
        stepperConfig: importStepper,
        title: t('employee:IMPORT_EMPLOYEES')
      },
      mapFields,
      confirm
    ]
    // external_benefit_budget: [
    //   {
    //     ...uploadFile,
    //     stepperConfig: importStepper,
    //     title: t('import:IMPORT_EXTERNAL_BENEFITS')
    //   },
    //   mapFields,
    //   confirm
    // ]
  };

  const [currentStep, setCurrentStep] = useState<StepDef>(step.employees[0]);

  useEffect(() => {
    setStepperConfig(importStepper);
    setBackToTitle(t('import:BACK_TO_OVERVIEW'));
    setBackToPath('/imports');
  }, []);

  useEffect(() => {
    newImport.type = params.type as ImportTypeEnum;
  }, [params.type]);

  useEffect(() => {
    if (params.type && !step[params.type as 'employees']) {
      navigate('/404');
    } else {
      const nextStep =
        params.type && params.step
          ? step[params.type as 'employees'][+params.step - 1]
          : currentStep;
      setCurrentStep(nextStep);

      nextStep.title !== undefined && setTitle(nextStep.title ?? undefined);
      nextStep.sectionTitle !== undefined && setSectionTitle(nextStep.sectionTitle ?? undefined);
      nextStep.stepperConfig && setStepperConfig(nextStep.stepperConfig);
    }
  }, [params]);

  useEffect(() => {
    return () => {
      if (impexStore.exportRequest) {
        impexStore.exportRequest = null;
      }
    };
  }, [impexStore]);

  const Step = currentStep?.content;

  return <Step />;
});
